import { controlServiceImpl } from "@lvt/streaming";
import { Box, IconButton, Typography, Popover, Fade, SvgIcon, List, ListItem, ListItemText, ListItemIcon } from "@material-ui/core";
import { ArrowDownward, Close, PlayCircleFilled } from "@material-ui/icons";
import Lottie from "lottie-react";
import { FC, useState } from "react";

import client from "@/api/client";
import { SpeakerSound } from "@/api/streamMetadata/types";
import soundWaveAnimation from "@/assets/animations/soundwave.json";
import { Sounds } from "@/common/components/SVG/Svg";
import * as FullStory from "@/utilities/hooks/fullstory";
import { OpenButton } from "@/utilities/types/streaming";

import useStyles from "./SoundsButton.styles";


interface SoundsButtonProps {
    cameraId: number;
    handleMenu: (button: OpenButton) => void;
    onError?: (error: any) => void;
    open: boolean;
    speakerSounds: SpeakerSound[];
}

const SoundsButton: FC<SoundsButtonProps> = ({ handleMenu, open, speakerSounds, cameraId, onError }) => {
    const maxHeight = speakerSounds.length > 4 ? 340 : undefined;
    const classes = useStyles({ maxHeight });

    const [playingId, setPlayingId] = useState<null | number>(null);

    const controlService = controlServiceImpl(client, cameraId);

    const handleClick = async (sound: SpeakerSound) => {
        setPlayingId(sound.id);

        try {
            await controlService.speakerSound(sound.id);
        } catch (e: any) {
            // TODO: Put back when edge stops returning errors every time
            // setPlayingId(null);

            if (onError) {
                onError(e.message);
            }
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" alignItems="center">
                <SvgIcon onClick={() => handleMenu(OpenButton.Sounds)} className={classes.buttonIcon}>
                    <Sounds />
                </SvgIcon>
                <Typography variant="caption">Sounds</Typography>
            </Box>

            <Popover
                classes={{ paper: classes.popover }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                open={open}
                TransitionComponent={Fade}
            >
                {/* Header */}
                <IconButton
                    aria-label="close"
                    onClick={() => handleMenu(OpenButton.None)}
                    className={classes.close}
                >
                    <Close />
                </IconButton>
                <div className={classes.header}>Sounds</div>

                {/* Content */}
                <List classes={{ root: classes.list }}>
                    {speakerSounds.map((sound) => (
                        <ListItem
                            key={sound.id}
                            button
                            onClick={() => handleClick(sound)}
                            disabled={playingId !== null && playingId !== sound.id}
                            className={FullStory.names.classes.streaming.quickSound}
                            disableGutters
                        >
                            <ListItemText>{sound.label}</ListItemText>
                            <ListItemIcon classes={{ root: classes.iconArea }}>
                                {playingId !== sound.id ? (
                                    <PlayCircleFilled classes={{ root: classes.playIcon }} />
                                ) : (
                                    <Lottie
                                        animationData={soundWaveAnimation}
                                        title="Quick Sound Playing"
                                        loop={false}
                                        onComplete={() => setPlayingId(null)}
                                        className={classes.soundAnimation}
                                    />
                                )}
                            </ListItemIcon>
                        </ListItem>
                    ))}
                </List>

                {/* Footer */}
                {maxHeight &&
                    <div className={classes.listFooter}>
                        <ArrowDownward />
                    </div>
                }
            </Popover>
        </>
    );
};

export default SoundsButton;
